import { useCallback } from 'react'

import { useStickyHeaderContext } from './StickyHeaderContext'

// Scrolls an invalid form element into view with full sticky header/toolbar awareness:
// suppresses the header reveal for the programmatic scroll (not a navigation intent),
// corrects the scroll position so the element lands below the sticky header/toolbar,
// and blurs the element if it is later scrolled back behind the header (dismissing
// the native validation tooltip).
//
// Two timing issues are handled internally:
// 1. scroll-behavior:smooth would fire scroll events across many frames — forced to
//    instant so only one event fires in Frame 1 (before the first rAF).
// 2. The correction scroll inside the first rAF queues its event for Frame 2 —
//    restoreReveal() runs in a nested rAF so it fires after Frame 2's scroll events.

type StickyHeaderSupport = {
  suppressReveal: () => void
  restoreReveal: () => void
  headerRef: { current: HTMLElement | null }
}

type TriggerScrollAndGetElement = () => HTMLElement | null | undefined

function getStickyTopOffset(headerEl: HTMLElement | null): number {
  const headerBottom = headerEl ? Math.max(0, headerEl.getBoundingClientRect().bottom) : 0
  const toolbar = document.querySelector<HTMLElement>('.faceted-search-toolbar-top-sticky, .toolbar-top-sticky')
  const toolbarBottom = toolbar ? Math.max(0, toolbar.getBoundingClientRect().bottom) : 0

  return Math.max(headerBottom, toolbarBottom)
}

let currentDismissOnScroll: (() => void) | null = null

function applyScrollCorrection(element: HTMLElement, headerEl: HTMLElement | null): void {
  const offset = getStickyTopOffset(headerEl)
  const rect = (element.closest('label') ?? element).getBoundingClientRect()

  if (rect.top < offset) {
    window.scrollTo({ top: window.scrollY + rect.top - offset - 16 })
  }

  if (currentDismissOnScroll) {
    window.removeEventListener('scroll', currentDismissOnScroll)
  }

  const dismissOnScroll = () => {
    if (element.getBoundingClientRect().top < getStickyTopOffset(headerEl)) {
      element.blur()
      window.removeEventListener('scroll', dismissOnScroll)
      currentDismissOnScroll = null
    }
  }
  currentDismissOnScroll = dismissOnScroll
  window.addEventListener('scroll', dismissOnScroll, { passive: true })
}

export function scrollInvalidElementIntoView(
  stickyHeaderContext: StickyHeaderSupport | undefined,
  triggerScrollAndGetElement: TriggerScrollAndGetElement,
): void {
  stickyHeaderContext?.suppressReveal()
  document.documentElement.style.scrollBehavior = 'auto'
  const element = triggerScrollAndGetElement()
  const headerEl = stickyHeaderContext?.headerRef.current ?? null

  requestAnimationFrame(() => {
    if (element) applyScrollCorrection(element, headerEl)
    document.documentElement.style.scrollBehavior = ''
    requestAnimationFrame(() => stickyHeaderContext?.restoreReveal())
  })
}

export function useScrollInvalidElementIntoView(): (triggerScrollAndGetElement: TriggerScrollAndGetElement) => void {
  const stickyHeaderContext = useStickyHeaderContext()

  return useCallback(
    (triggerScrollAndGetElement: TriggerScrollAndGetElement) => {
      scrollInvalidElementIntoView(stickyHeaderContext, triggerScrollAndGetElement)
    },
    [stickyHeaderContext],
  )
}
